import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card,
    Form,
    Input,
    Checkbox,
    Button,
    Space,
    Row,
    Col,
    Modal,
    Select,
    message,
    Image,
    Tooltip,
} from "antd";
import { SaveOutlined, SearchOutlined } from "@ant-design/icons";
import { getFactions, getSets, saveSearch, getEffects, api } from "../../api/api";
import { useTheme } from "../../context/ThemeContext";
import { EFFECT_DISPLAY_MAP, getQueryValue } from "../../config/EffectsDisplayConfig";

const { Option } = Select;

// ─── Constante séparateur de la sélection modale ──────────────────────────────
// Format : "TIMING|||CONDITION|||CONTENT" — jamais envoyé au back
const MODAL_SEP = "|||";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildEffectQuery({ timing, condition, content }) {
    const applyQueryValues = (val) => {
        if (!val) return "%";
        // Remplace chaque code connu par son queryValue si défini
        let result = val;
        Object.entries(EFFECT_DISPLAY_MAP)
            .sort((a, b) => b[0].length - a[0].length)
            .forEach(([code, { queryValue }]) => {
                if (!queryValue) return;
                const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                result = result.replace(new RegExp(escaped, "g"), queryValue);
            });
        return result || "%";
    };

    const parts = [
        applyQueryValues(timing),
        applyQueryValues(condition),
        applyQueryValues(content),
    ];
    if (parts.every((p) => p === "%")) return "";
    return parts.join("");
}

function decodeModalState(raw) {
    if (!raw || !raw.includes(MODAL_SEP))
        return { timing: "", condition: "", content: "" };
    const [timing = "", condition = "", content = ""] = raw.split(MODAL_SEP);
    return { timing, condition, content };
}

function encodeModalState({ timing, condition, content }) {
    return [timing, condition, content].join(MODAL_SEP);
}

/**
 * Tokenise un texte brut en segments { type, ... }.
 * Mutualisé entre renderOptionLabel et RichEffectInput.
 *
 * @param {string} text
 * @returns {{ type: "image"|"symbol"|"text", src?, alt?, text? }[]}
 */
function tokenizeEffectText(text) {
    if (!text) return [];

    const entries = Object.entries(EFFECT_DISPLAY_MAP).sort(
        (a, b) => b[0].length - a[0].length
    );

    const tokens = [];
    let remaining = text;

    while (remaining.length > 0) {
        let matched = false;
        for (const [code, { symbol, label }] of entries) {
            if (remaining.startsWith(code)) {
                const isImage = symbol && (symbol.startsWith("/") || symbol.startsWith("http"));
                if (isImage) {
                    tokens.push({ type: "image", src: symbol, alt: label || code });
                } else if (symbol) {
                    tokens.push({ type: "symbol", text: symbol });
                } else {
                    tokens.push({ type: "text", text: code });
                }
                remaining = remaining.slice(code.length);
                matched = true;
                break;
            }
        }
        if (!matched) {
            const last = tokens[tokens.length - 1];
            if (last?.type === "text") {
                last.text += remaining[0];
            } else {
                tokens.push({ type: "text", text: remaining[0] });
            }
            remaining = remaining.slice(1);
        }
    }
    return tokens;
}

/**
 * Rendu JSX des tokens pour les options de Select.
 * Retourne du texte simple s'il n'y a aucune image, sinon du JSX.
 */
function renderOptionLabel(text, isDarkMode = false) {
    const tokens = tokenizeEffectText(text);
    const hasImage = tokens.some((t) => t.type === "image");

    if (!hasImage) {
        return tokens.map((t) => (t.type === "symbol" ? `${t.text} ` : t.text)).join("");
    }

    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
      {tokens.map((token, i) => {
          if (token.type === "image") {
              return (
                  <img
                      key={i}
                      src={token.src}
                      alt={token.alt}
                      style={{
                          width: 16,
                          height: 16,
                          objectFit: "contain",
                          verticalAlign: "middle",
                          filter: isDarkMode ? "invert(1) hue-rotate(180deg)" : "none",
                      }}
                  />
              );
          }
          return <span key={i}>{token.type === "symbol" ? `${token.text} ` : token.text}</span>;
      })}
    </span>
    );
}

// ─── RichEffectInput ──────────────────────────────────────────────────────────
// Mode lecture : div avec symboles/images. Mode édition : input texte brut.
function RichEffectInput({ value, onChange, onClear, placeholder, addonAfter, isDarkMode }) {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);

    const handleDisplayClick = () => {
        setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const renderRichValue = (raw) => {
        if (!raw) return <span style={{ opacity: 0.4 }}>{placeholder}</span>;

        return tokenizeEffectText(raw).map((token, i) => {
            if (token.type === "image") {
                return (
                    <img
                        key={i}
                        src={token.src}
                        alt={token.alt}
                        style={{
                            width: 16,
                            height: 16,
                            objectFit: "contain",
                            verticalAlign: "middle",
                            margin: "0 1px",
                            filter: isDarkMode ? "invert(1) hue-rotate(180deg)" : "none",
                        }}
                    />
                );
            }
            if (token.type === "symbol") {
                return <span key={i} style={{ margin: "0 1px" }}>{token.text}</span>;
            }
            return <span key={i}>{token.text}</span>;
        });
    };

    const bg     = isDarkMode ? "#141414" : "#ffffff";
    const border = isDarkMode ? "#434343" : "#d9d9d9";
    const color  = isDarkMode ? "#ffffff" : "#000000";
    const sharedStyle = {
        width: "100%",
        padding: "4px 30px 4px 11px",
        height: 32,
        borderRight: "none",
        borderRadius: "6px 0 0 6px",
        background: bg,
        color,
        fontSize: 14,
        boxSizing: "border-box",
    };

    return (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <div style={{ flex: 1, position: "relative" }}>
                {editing ? (
                    <input
                        ref={inputRef}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onBlur={() => setEditing(false)}
                        placeholder={placeholder}
                        style={{
                            ...sharedStyle,
                            border: `1px solid ${isDarkMode ? "#177ddc" : "#4096ff"}`,
                            outline: "none",
                        }}
                    />
                ) : (
                    <div
                        onClick={handleDisplayClick}
                        style={{
                            ...sharedStyle,
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                            minHeight: 32,
                            border: `1px solid ${border}`,
                            cursor: "text",
                            userSelect: "none",
                        }}
                    >
                        {renderRichValue(value)}
                    </div>
                )}

                {value && (
                    <span
                        onClick={(e) => { e.stopPropagation(); onClear(); setEditing(false); }}
                        style={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#999",
                            fontSize: 12,
                            lineHeight: 1,
                            padding: "2px",
                        }}
                        title="Effacer"
                    >
            ✕
          </span>
                )}
            </div>

            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 32,
                padding: "0 8px",
                border: `1px solid ${border}`,
                borderLeft: "none",
                borderRadius: "0 6px 6px 0",
                background: isDarkMode ? "#2a2a2a" : "#fafafa",
                cursor: "pointer",
            }}>
                {addonAfter}
            </div>
        </div>
    );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function AdvancedSearchFilters({
                                                  onApply,
                                                  initialFilters = {},
                                                  isEditing = false,
                                                  searchId = null,
                                                  searchName = "",
                                                  onSaveSuccess = null,
                                              }) {
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const [form] = Form.useForm();

    const [factions, setFactions]         = useState([]);
    const [sets, setSets]                 = useState([]);
    const [selectedSets, setSelectedSets] = useState([]);
    const [showSaveModal, setShowSaveModal]   = useState(false);
    const [searchNameInput, setSearchNameInput] = useState(searchName);
    const [savingLoading, setSavingLoading]   = useState(false);

    const [effectsData, setEffectsData] = useState({
        timings: [],
        action_conditions: [],
        effect_contents: [],
    });

    // effectValues[i] : valeur brute affichée ET envoyée au back (display = query)
    const [effectValues, setEffectValues]           = useState(["", ""]);
    const [effectModalStates, setEffectModalStates] = useState([
        { timing: "", condition: "", content: "" },
        { timing: "", condition: "", content: "" },
    ]);
    const [effectModalOpen, setEffectModalOpen]   = useState(false);
    const [effectModalIndex, setEffectModalIndex] = useState(0);
    const [pendingModalState, setPendingModalState] = useState({
        timing: "", condition: "", content: "",
    });

    const [filters, setFilters] = useState({
        card_name: "",
        effects: ["", ""],
        _effectsModal: [
            encodeModalState({ timing: "", condition: "", content: "" }),
            encodeModalState({ timing: "", condition: "", content: "" }),
        ],
        factions: [],
        sets: [],
        forest: "", mountain: "", water: "", main: "", recall: "",
        in_sale: false, start_sell_today: false, price_change: false,
    });

    // ── Chargement des données ──────────────────────────────────────────────────

    useEffect(() => {
        getFactions()
            .then((res) => setFactions(Object.values(res.data)))
            .catch((err) => console.error("Erreur factions", err));
    }, []);

    useEffect(() => {
        getSets()
            .then((res) => setSets(res.data))
            .catch((err) => console.error("Erreur sets", err));
    }, []);

    useEffect(() => {
        if (typeof getEffects !== "function") {
            console.error("[AdvancedSearchFilters] Ajoutez : export const getEffects = () => api.get('/effects');");
            return;
        }
        getEffects()
            .then((res) => {
                const { timings = [], action_conditions = [], effect_contents = [] } = res.data;
                setEffectsData({ timings, action_conditions, effect_contents });
            })
            .catch((err) => console.error("Erreur effets", err));
    }, []);

    // ── Style dark mode des dropdowns Select ─────────────────────────────────
    useEffect(() => {
        const styleId = "effect-select-dropdown-style";
        let el = document.getElementById(styleId);
        if (!el) {
            el = document.createElement("style");
            el.id = styleId;
            document.head.appendChild(el);
        }
        el.textContent = isDarkMode ? `
      .effect-select-dropdown { background-color: #1f1f1f !important; border: 1px solid #444 !important; }
      .effect-select-dropdown .ant-select-item { color: #e0e0e0 !important; background-color: #1f1f1f !important; }
      .effect-select-dropdown .ant-select-item:hover,
      .effect-select-dropdown .ant-select-item-option-active:not(.ant-select-item-option-disabled) { background-color: #2e2e2e !important; color: #fff !important; }
      .effect-select-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) { background-color: #177ddc44 !important; color: #fff !important; }
      .effect-select-dropdown .ant-select-item-option-content { color: #e0e0e0 !important; }
      .effect-select-dropdown input { background-color: #2a2a2a !important; color: #e0e0e0 !important; }
    ` : `
      .effect-select-dropdown { background-color: #fff; }
      .effect-select-dropdown .ant-select-item { color: #000; }
    `;
    }, [isDarkMode]);

    // ── Hydratation des filtres initiaux ───────────────────────────────────────

    useEffect(() => {
        const initEffectsModal  = (initialFilters._effectsModal || ["", ""]).map(decodeModalState);
        const initEffectsValues = initialFilters.effects || ["", ""];

        setEffectModalStates(initEffectsModal);
        setEffectValues(initEffectsValues);

        setFilters({
            card_name: initialFilters.card_name || "",
            effects:   initEffectsValues,
            _effectsModal: initialFilters._effectsModal || ["", ""],
            factions:  initialFilters.factions || [],
            sets:      initialFilters.sets || [],
            forest:    initialFilters.forest || "",
            mountain:  initialFilters.mountain || "",
            water:     initialFilters.water || "",
            main:      initialFilters.main || "",
            recall:    initialFilters.recall || "",
            in_sale:         initialFilters.in_sale === true || initialFilters.in_sale === "true",
            start_sell_today: initialFilters.start_sell_today === true || initialFilters.start_sell_today === "true",
            price_change:    initialFilters.price_change === true || initialFilters.price_change === "true",
        });

        setSearchNameInput(searchName);
        if (initialFilters.sets) {
            setSelectedSets(sets.filter((s) => initialFilters.sets.includes(s.id)));
        }
    }, [initialFilters, searchName, sets]);

    // ── Gestion de la modal d'effet ─────────────────────────────────────────────

    const openEffectModal = (index) => {
        setEffectModalIndex(index);
        setPendingModalState({ ...effectModalStates[index] });
        setEffectModalOpen(true);
    };

    const handleEffectModalConfirm = () => {
        const queryValue = buildEffectQuery(pendingModalState);

        const newModalStates = [...effectModalStates];
        newModalStates[effectModalIndex] = { ...pendingModalState };
        setEffectModalStates(newModalStates);

        const newValues = [...effectValues];
        newValues[effectModalIndex] = queryValue;
        setEffectValues(newValues);

        setFilters((prev) => ({
            ...prev,
            effects: newValues,
            _effectsModal: newModalStates.map(encodeModalState),
        }));

        setEffectModalOpen(false);
    };

    const updateEffectValue = (index, value) => {
        const newValues = [...effectValues];
        newValues[index] = value;
        setEffectValues(newValues);
        setFilters((prev) => ({ ...prev, effects: newValues }));
    };

    const clearEffectValue = (index) => {
        const newModalStates = [...effectModalStates];
        newModalStates[index] = { timing: "", condition: "", content: "" };
        setEffectModalStates(newModalStates);

        const newValues = [...effectValues];
        newValues[index] = "";
        setEffectValues(newValues);

        setFilters((prev) => ({
            ...prev,
            effects: newValues,
            _effectsModal: newModalStates.map(encodeModalState),
        }));
    };

    // ── Gestion checkboxes ──────────────────────────────────────────────────────

    const handleCheckboxChange = (name, checked) => {
        if (name === "price_change") {
            setFilters((prev) => ({
                ...prev,
                price_change: checked,
                in_sale: checked ? false : prev.in_sale,
                start_sell_today: checked ? false : prev.start_sell_today,
            }));
        } else if (name === "in_sale" && filters.price_change) {
            return;
        } else {
            setFilters((prev) => ({ ...prev, [name]: checked }));
        }
    };

    // ── Application des filtres ─────────────────────────────────────────────────

    const handleApply = () => {
        const appliedFilters = {
            ...filters,
            effects: effectValues,
            sets: selectedSets.map((s) => s.id),
        };

        if (!appliedFilters.in_sale) delete appliedFilters.in_sale;
        if (!appliedFilters.start_sell_today) delete appliedFilters.start_sell_today;
        if (!appliedFilters.price_change) delete appliedFilters.price_change;

        const params = new URLSearchParams();
        Object.entries(appliedFilters).forEach(([key, value]) => {
            if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) return;
            if (Array.isArray(value)) {
                value.forEach((v) => v && params.append(`${key}[]`, v));
            } else {
                params.set(key, value);
            }
        });

        navigate(`/search?${params.toString()}`);
        onApply?.(appliedFilters);
    };

    // ── Sauvegarde ──────────────────────────────────────────────────────────────

    const handleSaveSearch = async () => {
        if (!searchNameInput.trim()) {
            message.error("Donne un nom à ta recherche");
            return;
        }

        const filtersToSave = {
            ...filters,
            effects: effectValues,
            _effectsModal: effectModalStates.map(encodeModalState),
        };

        try {
            setSavingLoading(true);
            if (isEditing && searchId) {
                await api.put(`/saved-searches/${searchId}`, { name: searchNameInput, filters: filtersToSave });
                message.success("Recherche mise à jour !");
            } else {
                await saveSearch(searchNameInput, filtersToSave);
                message.success("Recherche enregistrée !");
            }
            setSearchNameInput("");
            setShowSaveModal(false);
            if (onSaveSuccess) onSaveSuccess();
        } catch (err) {
            console.error(err);
            message.error("Erreur lors de l'enregistrement");
        } finally {
            setSavingLoading(false);
        }
    };

    // ── Rendu ───────────────────────────────────────────────────────────────────

    // Props communs aux 3 selects de la modal
    const selectCommonProps = {
        allowClear: true,
        showSearch: true,
        style: { width: "100%" },
        popupClassName: "effect-select-dropdown",
        // On filtre sur "label" (texte brut) et non "children" qui peut être du JSX
        optionFilterProp: "label",
    };

    return (
        <Card style={{ marginBottom: 16, background: isDarkMode ? "#1f1f1f" : "#ffffff" }}>
            <Form layout="vertical" form={form}>

                {/* Nom de la carte */}
                <Form.Item label="Nom de la carte">
                    <Input
                        placeholder="Chercher par nom"
                        value={filters.card_name || ""}
                        onChange={(e) => setFilters({ ...filters, card_name: e.target.value })}
                    />
                </Form.Item>

                {/* Sets */}
                <div style={{ marginBottom: 16 }}>
                    <h4>Sets</h4>
                    <Space wrap>
                        {sets.map((s) => {
                            const isSelected = selectedSets.some((sel) => sel.id === s.id);
                            return (
                                <Button
                                    key={s.id}
                                    type={isSelected ? "primary" : "default"}
                                    onClick={() => {
                                        const newSel = isSelected
                                            ? selectedSets.filter((sel) => sel.id !== s.id)
                                            : [...selectedSets, s];
                                        setSelectedSets(newSel);
                                        setFilters({ ...filters, sets: newSel.map((x) => x.id) });
                                    }}
                                >
                                    {s.name} ({s.code})
                                </Button>
                            );
                        })}
                    </Space>
                </div>

                {/* Factions */}
                <div style={{ marginBottom: 16 }}>
                    <h4>Factions</h4>
                    <Space wrap>
                        {factions.map((f) => {
                            const isSelected = filters.factions.includes(f.id);
                            return (
                                <Button
                                    key={f.id}
                                    type={isSelected ? "primary" : "default"}
                                    onClick={() => {
                                        const newFactions = isSelected
                                            ? filters.factions.filter((id) => id !== f.id)
                                            : [...filters.factions, f.id];
                                        setFilters({ ...filters, factions: newFactions });
                                    }}
                                >
                                    <Image src={f.logo} alt={f.name} width={24} preview={false} />
                                    {f.name}
                                </Button>
                            );
                        })}
                    </Space>
                </div>

                {/* Attributs */}
                <Row gutter={[16, 16]}>
                    {["forest", "mountain", "water", "main", "recall"].map((attr) => (
                        <Col key={attr} xs={12} sm={12} md={8} lg={6}>
                            <Input
                                placeholder={`${attr.charAt(0).toUpperCase() + attr.slice(1)} (min-max)`}
                                value={filters[attr]}
                                onChange={(e) => setFilters({ ...filters, [attr]: e.target.value })}
                            />
                        </Col>
                    ))}
                </Row>

                {/* Effets */}
                <Form.Item label="Effets" style={{ marginTop: 16 }}>
                    <Row gutter={[16, 12]}>
                        {[0, 1].map((index) => (
                            <Col key={index} xs={24} sm={12}>
                                <RichEffectInput
                                    placeholder={`Effet ${index + 1}`}
                                    value={effectValues[index]}
                                    isDarkMode={isDarkMode}
                                    onChange={(val) => updateEffectValue(index, val)}
                                    onClear={() => clearEffectValue(index)}
                                    addonAfter={
                                        <Tooltip
                                            title="Sélectionner via les catégories d'effets"
                                            overlayInnerStyle={{
                                                color: "#fff",
                                                backgroundColor: isDarkMode ? "#3a3a3a" : undefined,
                                            }}
                                        >
                                            <Button
                                                type="text"
                                                icon={<SearchOutlined style={{ color: isDarkMode ? "#aaa" : undefined }} />}
                                                size="small"
                                                onClick={() => openEffectModal(index)}
                                                style={{ border: "none", padding: "0 4px" }}
                                            />
                                        </Tooltip>
                                    }
                                />
                            </Col>
                        ))}
                    </Row>
                </Form.Item>

                {/* Statuts */}
                <Space direction="vertical" style={{ width: "100%", marginTop: 16 }}>
                    <Checkbox
                        checked={filters.in_sale}
                        onChange={(e) => handleCheckboxChange("in_sale", e.target.checked)}
                        disabled={filters.price_change}
                    >
                        En vente uniquement
                    </Checkbox>
                    <Checkbox
                        checked={filters.start_sell_today}
                        onChange={(e) => handleCheckboxChange("start_sell_today", e.target.checked)}
                        disabled={filters.price_change}
                    >
                        Mise en vente aujourd'hui
                    </Checkbox>
                    <Checkbox
                        checked={filters.price_change}
                        onChange={(e) => handleCheckboxChange("price_change", e.target.checked)}
                        disabled={filters.start_sell_today}
                    >
                        Changement de prix aujourd'hui
                    </Checkbox>
                </Space>

                {/* Boutons */}
                <Space style={{ marginTop: 16, width: "100%" }} wrap>
                    <Button type="primary" onClick={handleApply} size="large">
                        Appliquer les filtres
                    </Button>
                    <Button icon={<SaveOutlined />} onClick={() => setShowSaveModal(true)} size="large">
                        {isEditing ? "Mettre à jour" : "Enregistrer"}
                    </Button>
                </Space>
            </Form>

            {/* Modal de sélection d'effet */}
            <Modal
                title={`Sélectionner un effet (Slot ${effectModalIndex + 1})`}
                open={effectModalOpen}
                onOk={handleEffectModalConfirm}
                onCancel={() => setEffectModalOpen(false)}
                okText="Valider"
                cancelText="Annuler"
                width={640}
                destroyOnClose={false}
            >
                <Space direction="vertical" style={{ width: "100%" }} size="large">
                    <div style={{ fontSize: 12, color: "#888" }}>
                        Les champs laissés vides seront remplacés par <code>%</code> (correspondance partielle).
                        Laissez tout vide pour réinitialiser ce filtre d'effet.
                    </div>

                    <Form.Item label="⏱ Déclencheur" style={{ marginBottom: 0 }}>
                        <Select
                            {...selectCommonProps}
                            placeholder="Sélectionner un timing…"
                            value={pendingModalState.timing || undefined}
                            onChange={(val) => setPendingModalState((p) => ({ ...p, timing: val || "" }))}
                        >
                            {effectsData.timings
                                .filter((t) => t.name && t.name !== "[]")
                                .map((t) => (
                                    <Option key={t.id} value={t.name} label={t.name}>
                                        {renderOptionLabel(t.name, isDarkMode)}
                                    </Option>
                                ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="❓ Condition" style={{ marginBottom: 0 }}>
                        <Select
                            {...selectCommonProps}
                            placeholder="Sélectionner une condition…"
                            value={pendingModalState.condition || undefined}
                            onChange={(val) => setPendingModalState((p) => ({ ...p, condition: val || "" }))}
                        >
                            {effectsData.action_conditions
                                .filter((c) => c.text)
                                .map((c) => (
                                    <Option key={c.id} value={c.text} label={c.text}>
                                        {renderOptionLabel(c.text, isDarkMode)}
                                    </Option>
                                ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="✨ Contenu de l'effet" style={{ marginBottom: 0 }}>
                        <Select
                            {...selectCommonProps}
                            placeholder="Sélectionner un contenu…"
                            value={pendingModalState.content || undefined}
                            onChange={(val) => setPendingModalState((p) => ({ ...p, content: val || "" }))}
                        >
                            {effectsData.effect_contents
                                .filter((e) => e.text && e.text !== "[]")
                                .map((e) => (
                                    <Option key={e.id} value={e.text} label={e.text}>
                                        {renderOptionLabel(e.text, isDarkMode)}
                                    </Option>
                                ))}
                        </Select>
                    </Form.Item>

                    {(pendingModalState.timing || pendingModalState.condition || pendingModalState.content) && (
                        <div style={{
                            padding: "8px 12px",
                            background: isDarkMode ? "#2a2a2a" : "#f5f5f5",
                            borderRadius: 6,
                            fontSize: 13,
                        }}>
                            <strong>Aperçu (envoyé au back) :</strong>
                            <br />
                            <code style={{ wordBreak: "break-all" }}>
                                {buildEffectQuery(pendingModalState) || "(vide)"}
                            </code>
                        </div>
                    )}
                </Space>
            </Modal>

            {/* Modal de sauvegarde */}
            <Modal
                title={isEditing ? "Mettre à jour la recherche" : "Enregistrer la recherche"}
                open={showSaveModal}
                onOk={handleSaveSearch}
                onCancel={() => setShowSaveModal(false)}
                okText={isEditing ? "Mettre à jour" : "Enregistrer"}
                cancelText="Annuler"
                confirmLoading={savingLoading}
            >
                <Input
                    placeholder="Nom de la recherche"
                    value={searchNameInput}
                    onChange={(e) => setSearchNameInput(e.target.value)}
                    onPressEnter={handleSaveSearch}
                />
            </Modal>
        </Card>
    );
}